import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import config from '../../config/config';
import DashboardLayout from '../shared/layout/DashboardLayout';
import { getAdminMenuItems } from '../../utils/menuItems';
import '../../styles/ManajemenDataPage.css';

const ManajemenDataPage = () => {
    const [activeTab, setActiveTab] = useState('kl');
    const [klData, setKlData] = useState([]);
    const [dataIgt, setDataIgt] = useState([]);
    const [checklistData, setChecklistData] = useState([]);
    const [kepkaData, setKepkaData] = useState([]);
    const [loading, setLoading] = useState(false);

    // State untuk Modals
    const [showModal, setShowModal] = useState(null);
    const [currentItem, setCurrentItem] = useState(null);
    const [formData, setFormData] = useState({});
    const [usageData, setUsageData] = useState(null);

    // State untuk Pencarian dan Paginasi
    const [searchTermKL, setSearchTermKL] = useState('');
    const [searchTermIGT, setSearchTermIGT] = useState('');
    const [searchTermKepka, setSearchTermKepka] = useState('');
    const [searchTermChecklist, setSearchTermChecklist] = useState('');
    const [currentPageKL, setCurrentPageKL] = useState(1);
    const [currentPageIGT, setCurrentPageIGT] = useState(1);
    const [currentPageKepka, setCurrentPageKepka] = useState(1);
    const [currentPageChecklist, setCurrentPageChecklist] = useState(1);
    const itemsPerPage = 8;
    const [viewMode, setViewMode] = useState('tabel');
    
    // State untuk Filter
    const [filterJenisKSP, setFilterJenisKSP] = useState('');
    const [filterKL, setFilterKL] = useState('');
    const [filterRenaksiWalidata, setFilterRenaksiWalidata] = useState('');

    // State baru untuk sortir
    const [sortConfigKL, setSortConfigKL] = useState({ key: 'nama_kl', direction: 'ascending' });
    const [sortConfigIGT, setSortConfigIGT] = useState({ key: 'nama_igt', direction: 'ascending' });
    const [sortConfigKepka, setSortConfigKepka] = useState({ key: 'no', direction: 'ascending' });

    // State untuk input capaian dan target multi-tahun
    const [capaianYears, setCapaianYears] = useState([new Date().getFullYear()]);
    const [targetYears, setTargetYears] = useState([new Date().getFullYear()]);

    const navigate = useNavigate();

    // Fetch data awal
    useEffect(() => {
        fetchKL();
        fetchDataIGT();
        if (activeTab === 'checklist') {
            fetchChecklist();
        }
        if (activeTab === 'kepka') {
            fetchKepka();
        }
    }, [activeTab]);

    const getAuthHeaders = () => {
        const token = localStorage.getItem('token');
        return {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
        };
    };

    const fetchKL = async () => {
        setLoading(true);
        try {
            const response = await axios.get(`${config.API_URL}/api/management/kl`, { headers: getAuthHeaders() });
            if (response.data.success) {
                setKlData(response.data.kl);
            }
        } catch (error) {
            handleApiError(error, 'Gagal mengambil data KL');
        } finally {
            setLoading(false);
        }
    };

    const fetchDataIGT = async () => {
        setLoading(true);
        try {
            const response = await axios.get(`${config.API_URL}/api/management/data-igt`, { headers: getAuthHeaders() });
            if (response.data.success) {
                setDataIgt(response.data.data_igt);
            }
        } catch (error) {
            handleApiError(error, 'Gagal mengambil data IGT');
        } finally {
            setLoading(false);
        }
    };

    const fetchChecklist = async () => {
        setLoading(true);
        try {
            const response = await axios.get(`${config.API_URL}/api/checklist/all`, { headers: getAuthHeaders() });
            if (response.data.success) {
                setChecklistData(response.data.checklist);
            }
        } catch (error) {
            handleApiError(error, 'Gagal mengambil data checklist');
        } finally {
            setLoading(false);
        }
    };

    const fetchKepka = async () => {
        setLoading(true);
        try {
            const response = await axios.get(`${config.API_URL}/api/management/kepka`, { headers: getAuthHeaders() });
            if (response.data.success) {
                setKepkaData(response.data.data);
            }
        } catch (error) {
            handleApiError(error, 'Gagal mengambil data kepka');
        } finally {
            setLoading(false);
        }
    };

    const handleApiError = (error, defaultMessage) => {
        const errorMessage = error.response?.data?.message || defaultMessage;
        toast.error(errorMessage);
    };

    const showSuccessMessage = (msg) => {
        toast.success(msg);
    };

    // Fungsi-fungsi Modal
    const openModal = (type, item = null) => {
        setShowModal(type);
        setCurrentItem(item);
        if (type.includes('add') || type.includes('edit')) {
            if (item) {
                if (type === 'editIGT') {
                    const editData = { 
                        ...item, 
                        new_kode_igt: item.kode_igt,
                        nomor_kepka: item.nomor_kepka || '',
                        jenis_sk: item.jenis_sk || '',
                        form_mode: 'igt' 
                    };
                    setFormData(editData);

                    if (item.target && typeof item.target === 'object') {
                        const years = Object.keys(item.target);
                        if (years.length > 0) {
                            setTargetYears(years);
                            years.forEach(year => {
                                editData[`target_${year}`] = item.target[year];
                            });
                            setFormData(editData);
                        } else {
                            setTargetYears([new Date().getFullYear()]);
                        }
                    } else {
                        setTargetYears([new Date().getFullYear()]);
                    }
                } else if (type === 'editKepka') {
                    setFormData(item);
                    if (item.capaian) {
                        setCapaianYears(Object.keys(item.capaian));
                    }
                    if (item.target) {
                        setTargetYears(Object.keys(item.target));
                    }
                } else {
                    setFormData(item);
                }
            } else {
                if (type === 'addKL') {
                    setFormData({ kode_kl: '', nama_kl: '' });
                } else if (type === 'addChecklist') {
                    setFormData({ document_type: '', priority: 'mandatory', description: '', is_active: true });
                } else if (type === 'addKepka') {
                    setFormData({ no: '', kode_igt: '', jenis: '', nama_igt_walidata: '', satuan: '' });
                    setCapaianYears([new Date().getFullYear()]);
                    setTargetYears([new Date().getFullYear()]);
                } else {
                    setFormData({ kode_kl: '', nama_igt: '', kode_igt: '', jenis_ksp: '', form_mode: 'igt' });
                    setTargetYears([new Date().getFullYear()]);
                }
            }
        }
        if (type.includes('usage')) {
            fetchUsageData(type, item);
        }
    };

    const closeModal = () => {
        setShowModal(null);
        setCurrentItem(null);
        setFormData({});
        setUsageData(null);
        setCapaianYears([new Date().getFullYear()]);
        setTargetYears([new Date().getFullYear()]);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        
        if (name.startsWith('target_') || name.startsWith('capaian_')) {
            const cleanValue = value.replace(/[^\d.]/g, '');
            setFormData(prev => ({ ...prev, [name]: cleanValue }));
        } else {
            setFormData(prev => ({ ...prev, [name]: value }));
        }
    };

    // --- CRUD KL ---
    const handleAddKL = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const response = await axios.post(`${config.API_URL}/api/management/kl`, formData, { headers: getAuthHeaders() });
            showSuccessMessage(response.data.message);
            fetchKL();
            closeModal();
        } catch (error) {
            handleApiError(error, 'Gagal menambah KL');
        } finally {
            setLoading(false);
        }
    };

    const handleEditKL = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const response = await axios.put(`${config.API_URL}/api/management/kl/${currentItem.kode_kl}`, formData, { headers: getAuthHeaders() });
            showSuccessMessage(response.data.message);
            fetchKL();
            closeModal();
        } catch (error) {
            handleApiError(error, 'Gagal mengedit KL');
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteKL = async () => {
        setLoading(true);
        try {
            const response = await axios.delete(`${config.API_URL}/api/management/kl/${currentItem.kode_kl}`, { headers: getAuthHeaders() });
            showSuccessMessage(response.data.message);
            fetchKL();
            closeModal();
        } catch (error) {
            handleApiError(error, 'Gagal menghapus KL');
        } finally {
            setLoading(false);
        }
    };

    // --- CRUD Data IGT ---
    const handleAddIGT = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const target = {};
            targetYears.forEach(year => {
                if (formData[`target_${year}`]) {
                    target[year] = parseFloat(formData[`target_${year}`]) || 0;
                }
            });

            let payload;
            
            // Matriks data berdasarkan Kelompok IGT
            if (formData.jenis_sk === 'Renaksi') {
                // Renaksi
                payload = {
                    form_mode: 'matriks',
                    jenis_sk: formData.jenis_sk,
                    kode_igt: formData.kode_igt,
                    kode_kl: formData.kode_kl,
                    nama_igt_renaksi: formData.nama_igt_renaksi || '', // field dari form adalah nama_igt_renaksi
                    nama_igt_walidata: '',
                    jenis_ksp: formData.jenis_ksp || 'Renaksi PKSP',
                    nomor_kepka: formData.nomor_kepka || '',
                    satuan: formData.satuan,
                    target: target
                };
            } else if (formData.jenis_sk === 'Walidata') {
                // Walidata
                payload = {
                    form_mode: 'matriks',
                    jenis_sk: formData.jenis_sk,
                    kode_igt: formData.kode_igt,
                    kode_kl: formData.kode_kl,
                    nama_igt_walidata: formData.nama_igt_walidata || '',
                    nama_igt_renaksi: '',
                    jenis_ksp: formData.jenis_ksp,
                    nomor_kepka: formData.nomor_kepka || '',
                    satuan: formData.satuan || '', // opsional
                    target: Object.keys(target).length > 0 ? target : null // opsional
                };
            } else if (formData.jenis_sk === 'Renaksi dan Walidata') {
                // Renaksi dan Walidata
                payload = {
                    form_mode: 'matriks',
                    jenis_sk: formData.jenis_sk,
                    kode_igt: formData.kode_igt,
                    kode_kl: formData.kode_kl,
                    nama_igt_walidata: formData.nama_igt_walidata || '',
                    nama_igt_renaksi: formData.nama_igt_renaksi || '', // opsional
                    jenis_ksp: formData.jenis_ksp,
                    nomor_kepka: formData.nomor_kepka || '',
                    satuan: formData.satuan || '', // opsional
                    target: Object.keys(target).length > 0 ? target : null // opsional
                };
            } else {
                // Fallback: Mode IGT biasa (tanpa Kelompok IGT)
                payload = {
                    form_mode: 'igt',
                    kode_kl: formData.kode_kl,
                    kode_igt: formData.kode_igt,
                    nama_igt: formData.nama_igt,
                    jenis_ksp: formData.jenis_ksp
                };
            }

            const response = await axios.post(`${config.API_URL}/api/management/data-igt`, payload, { headers: getAuthHeaders() });
            showSuccessMessage(response.data.message);
            fetchDataIGT();
            closeModal();
        } catch (error) {
            handleApiError(error, 'Gagal menambah Data IGT');
        } finally {
            setLoading(false);
        }
    };

    const handleEditIGT = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const target = {};
            targetYears.forEach(year => {
                if (formData[`target_${year}`]) {
                    target[year] = parseFloat(formData[`target_${year}`]) || 0;
                }
            });

            const payload = {
                kode_kl: formData.kode_kl,
                kode_igt: formData.kode_igt,
                nama_igt: formData.nama_igt,
                jenis_ksp: formData.jenis_ksp || (formData.jenis_sk === 'Renaksi' ? 'Renaksi PKSP' : ''),
                // Matriks data
                kepka_id: formData.kepka_id,
                jenis_sk: formData.jenis_sk,
                nomor_kepka: formData.nomor_kepka,
                nama_igt_walidata: formData.nama_igt_walidata,
                nama_igt_renaksi: formData.nama_igt_renaksi,
                satuan: formData.satuan,
                target: target
            };

            const response = await axios.put(`${config.API_URL}/api/management/data-igt/${currentItem.kode_kl}/${currentItem.kode_igt}`, payload, { headers: getAuthHeaders() });
            showSuccessMessage(response.data.message);
            fetchDataIGT();
            closeModal();
        } catch (error) {
            handleApiError(error, 'Gagal mengedit Data IGT');
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteIGT = async () => {
        setLoading(true);
        try {
            const response = await axios.delete(`${config.API_URL}/api/management/data-igt/${currentItem.kode_kl}/${currentItem.kode_igt}`, { headers: getAuthHeaders() });
            showSuccessMessage(response.data.message);
            fetchDataIGT();
            closeModal();
        } catch (error) {
            handleApiError(error, 'Gagal menghapus Data IGT');
        } finally {
            setLoading(false);
        }
    };

    // --- Usage Data ---
    const fetchUsageData = async (type, item) => {
        setLoading(true);
        setUsageData(null);
        let url = '';
        if (type === 'usageKL') {
            url = `${config.API_URL}/api/management/kl/${item.kode_kl}/usage`;
        } else if (type === 'usageIGT') {
            url = `${config.API_URL}/api/management/data-igt/${item.kode_kl}/${item.kode_igt}/usage`;
        }

        try {
            const response = await axios.get(url, { headers: getAuthHeaders() });
            if (response.data.success) {
                setUsageData(response.data.usage);
            }
        } catch (error) {
            handleApiError(error, 'Gagal mengambil data penggunaan');
        } finally {
            setLoading(false);
        }
    };

    // --- Filtering, Sorting, and Pagination Logic ---
    const filteredKL = klData.filter(kl =>
        (kl.nama_kl || '').toLowerCase().includes(searchTermKL.toLowerCase()) ||
        (kl.kode_kl || '').toLowerCase().includes(searchTermKL.toLowerCase())
    );
    const filteredIGT = dataIgt.filter(igt => {
        const namaIgt = (igt.nama_igt || '').toLowerCase();
        const kodeIgt = (igt.kode_igt || '').toLowerCase();
        const namaKl = (igt.nama_kl || '').toLowerCase();
        const searchLower = searchTermIGT.toLowerCase();
        
        const matchesSearch = !searchTermIGT || 
            namaIgt.includes(searchLower) ||
            kodeIgt.includes(searchLower) ||
            namaKl.includes(searchLower);
        
        const matchesJenisKSP = !filterJenisKSP || igt.jenis_ksp === filterJenisKSP;
        const matchesKL = !filterKL || igt.kode_kl === filterKL;
        const matchesRenaksiWalidata = !filterRenaksiWalidata || igt.jenis_sk === filterRenaksiWalidata;
        
        return matchesSearch && matchesJenisKSP && matchesKL && matchesRenaksiWalidata;
    });
    const filteredKepka = kepkaData.filter(kepka =>
        kepka.nama_igt_walidata?.toLowerCase().includes(searchTermKepka.toLowerCase()) ||
        kepka.kode_igt?.toLowerCase().includes(searchTermKepka.toLowerCase()) ||
        kepka.jenis?.toLowerCase().includes(searchTermKepka.toLowerCase()) ||
        kepka.no?.toString().includes(searchTermKepka)
    );
    
    const filteredChecklist = checklistData.filter(item => {
        const matchesSearch = item.document_type?.toLowerCase().includes(searchTermChecklist.toLowerCase()) ||
            item.description?.toLowerCase().includes(searchTermChecklist.toLowerCase());
        return matchesSearch;
    });

    const sortedKL = useMemo(() => {
        let sortableItems = [...filteredKL];
        if (sortConfigKL.key) {
            sortableItems.sort((a, b) => {
                const valA = a[sortConfigKL.key] || '';
                const valB = b[sortConfigKL.key] || '';
                if (valA < valB) {
                    return sortConfigKL.direction === 'ascending' ? -1 : 1;
                }
                if (valA > valB) {
                    return sortConfigKL.direction === 'ascending' ? 1 : -1;
                }
                return 0;
            });
        }
        return sortableItems;
    }, [filteredKL, sortConfigKL]);

    const sortedIGT = useMemo(() => {
        let sortableItems = [...filteredIGT];
        if (sortConfigIGT.key) {
            sortableItems.sort((a, b) => {
                const valA = a[sortConfigIGT.key] || '';
                const valB = b[sortConfigIGT.key] || '';
                if (valA < valB) {
                    return sortConfigIGT.direction === 'ascending' ? -1 : 1;
                }
                if (valA > valB) {
                    return sortConfigIGT.direction === 'ascending' ? 1 : -1;
                }
                return 0;
            });
        }
        return sortableItems;
    }, [filteredIGT, sortConfigIGT]);

    const sortedKepka = useMemo(() => {
        let sortableItems = [...filteredKepka];
        if (sortConfigKepka.key) {
            sortableItems.sort((a, b) => {
                const valA = a[sortConfigKepka.key] || '';
                const valB = b[sortConfigKepka.key] || '';
                if (valA < valB) {
                    return sortConfigKepka.direction === 'ascending' ? -1 : 1;
                }
                if (valA > valB) {
                    return sortConfigKepka.direction === 'ascending' ? 1 : -1;
                }
                return 0;
            });
        }
        return sortableItems;
    }, [filteredKepka, sortConfigKepka]);

    const requestSortKepka = (key) => {
        let direction = 'ascending';
        if (sortConfigKepka.key === key && sortConfigKepka.direction === 'ascending') {
            direction = 'descending';
        }
        setSortConfigKepka({ key, direction });
    };

    const requestSortKL = (key) => {
        let direction = 'ascending';
        if (sortConfigKL.key === key && sortConfigKL.direction === 'ascending') {
            direction = 'descending';
        }
        setSortConfigKL({ key, direction });
    };

    const requestSortIGT = (key) => {
        let direction = 'ascending';
        if (sortConfigIGT.key === key && sortConfigIGT.direction === 'ascending') {
            direction = 'descending';
        }
        setSortConfigIGT({ key, direction });
    };

    const getSortIcon = (config, key) => {
        if (config.key !== key) {
            return <i className="fas fa-sort sort-icon-default"></i>;
        }
        return config.direction === 'ascending' ?
            <i className="fas fa-sort-up"></i> :
            <i className="fas fa-sort-down"></i>;
    };

    const paginate = (data, currentPage) => {
        const startIndex = (currentPage - 1) * itemsPerPage;
        return data.slice(startIndex, startIndex + itemsPerPage);
    };

    const currentKL = paginate(sortedKL, currentPageKL);
    const totalPagesKL = Math.ceil(sortedKL.length / itemsPerPage);
    const currentIGT = paginate(sortedIGT, currentPageIGT);
    const totalPagesIGT = Math.ceil(sortedIGT.length / itemsPerPage);
    const currentKepka = paginate(sortedKepka, currentPageKepka);
    const totalPagesKepka = Math.ceil(sortedKepka.length / itemsPerPage);

    const renderPagination = (currentPage, totalPages, setCurrentPage) => {
        if (totalPages <= 1) return null;
        return (
            <>
                <div className="data-admin-pagination">
                    <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} className="data-admin-pagination-btn">
                        <i className="fas fa-chevron-left"></i> Previous
                    </button>
                    <div className="data-admin-pagination-numbers">
                    </div>
                    <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} className="data-admin-pagination-btn">
                        Next <i className="fas fa-chevron-right"></i>
                    </button>
                </div>
                <div className="data-admin-page-info">
                    Halaman {currentPage} dari {totalPages}
                </div>
            </>
        );
    };

    // --- Render Functions ---
    const renderViewSwitcher = () => (
        <div className="data-admin-view-switcher">
            <div className="view-switcher-buttons">
                <button
                    className={`data-admin-view-btn ${viewMode === 'tabel' ? 'active' : ''}`}
                    onClick={() => setViewMode('tabel')}
                    title="Tampilan Tabel"
                >
                    <i className="fas fa-list"></i> Tabel
                </button>
                <button
                    className={`data-admin-view-btn ${viewMode === 'kartu' ? 'active' : ''}`}
                    onClick={() => setViewMode('kartu')}
                    title="Tampilan Kartu"
                >
                    <i className="fas fa-th-large"></i> Kartu
                </button>
            </div>
        </div>
    );

    const NoResultsFound = ({ term, type }) => (
        <div className="data-admin-no-results">
            <div className="data-admin-no-results-icon"><i className="fas fa-search"></i></div>
            <h4>Tidak Ditemukan</h4>
            <p>{term ? `Tidak ada ${type} dengan kriteria "${term}"` : `Belum ada data ${type}.`}</p>
        </div>
    );

    // Render KL
    const renderKLAsCards = () => (
        <div className="data-admin-items-grid">
            {currentKL.map((kl, index) => (
                <div key={`kl-${kl.kode_kl}-${index}`} className="data-admin-item-card">
                    <div className="data-admin-item-header">
                        <div className="data-admin-item-icon"><i className="fas fa-building"></i></div>
                        <div className="data-admin-item-actions">
                            <button className="data-admin-action-btn data-admin-info-btn" title="Lihat Penggunaan" onClick={() => openModal('usageKL', kl)}><i className="fas fa-info-circle"></i></button>
                            <button className="data-admin-action-btn data-admin-edit-btn" title="Edit KL" onClick={() => openModal('editKL', kl)}><i className="fas fa-pencil-alt"></i></button>
                            <button className="data-admin-action-btn data-admin-delete-btn" title="Hapus KL" onClick={() => openModal('deleteKL', kl)}><i className="fas fa-trash-alt"></i></button>
                        </div>
                    </div>
                    <div className="data-admin-item-content">
                        <h4>{kl.nama_kl}</h4>
                        <p className="data-admin-item-code">Kode: {kl.kode_kl}</p>
                    </div>
                </div>
            ))}
        </div>
    );

    const renderKLAsTable = () => (
        <div className="data-admin-table-container">
            <table className="data-admin-table" style={{ tableLayout: 'fixed', width: '100%' }}>
                <thead>
                    <tr>
                        <th style={{ width: '60px' }}>No</th>
                        <th style={{ width: '120px' }} onClick={() => requestSortKL('kode_kl')} className={`sortable-header ${sortConfigKL.key === 'kode_kl' ? 'active' : ''}`}>
                            Kode KL {getSortIcon(sortConfigKL, 'kode_kl')}
                        </th>
                        <th style={{ width: 'auto' }} onClick={() => requestSortKL('nama_kl')} className={`sortable-header ${sortConfigKL.key === 'nama_kl' ? 'active' : ''}`}>
                            Nama Kementerian/Lembaga {getSortIcon(sortConfigKL, 'nama_kl')}
                        </th>
                        <th style={{ width: '120px' }} className="data-admin-table-actions-header">Aksi</th>
                    </tr>
                </thead>
                <tbody>
                    {currentKL.map((kl, index) => (
                        <tr key={`kl-row-${kl.kode_kl}-${index}`}>
                            <td>{(currentPageKL - 1) * itemsPerPage + index + 1}</td>
                            <td><span className="data-admin-table-code">{kl.kode_kl}</span></td>
                            <td style={{ wordWrap: 'break-word', overflow: 'hidden' }}>{kl.nama_kl}</td>
                            <td>
                                <div className="data-admin-item-actions">
                                    <button className="data-admin-action-btn data-admin-info-btn" title="Lihat Penggunaan" onClick={() => openModal('usageKL', kl)}><i className="fas fa-info-circle"></i></button>
                                    <button className="data-admin-action-btn data-admin-edit-btn" title="Edit KL" onClick={() => openModal('editKL', kl)}><i className="fas fa-pencil-alt"></i></button>
                                    <button className="data-admin-action-btn data-admin-delete-btn" title="Hapus KL" onClick={() => openModal('deleteKL', kl)}><i className="fas fa-trash-alt"></i></button>
                                </div>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );

    const renderKLTab = () => (
        <>
            <div className="data-admin-controls">
                <div className="data-admin-search-container">
                    <div className="data-admin-search-input-group">
                        <i className="fas fa-search data-admin-search-icon"></i>
                        <input
                            type="text"
                            placeholder="Cari berdasarkan Kode atau Nama KL..."
                            className="data-admin-search-input"
                            value={searchTermKL}
                            onChange={(e) => { setSearchTermKL(e.target.value); setCurrentPageKL(1); }}
                        />
                        {searchTermKL && <button className="data-admin-search-clear" onClick={() => setSearchTermKL('')}><i className="fas fa-times"></i></button>}
                    </div>
                </div>
                {renderViewSwitcher()}
            </div>

            {currentKL.length > 0 ? (
                viewMode === 'tabel' ? renderKLAsTable() : renderKLAsCards()
            ) : (
                <NoResultsFound term={searchTermKL} type="KL" />
            )}

            {renderPagination(currentPageKL, totalPagesKL, setCurrentPageKL)}
        </>
    );

    // Render IGT
    const renderIGTAsCards = () => (
        <div className="data-admin-items-grid">
            {currentIGT.map((igt, index) => (
                <div key={`igt-${igt.kode_kl}-${igt.kode_igt}-${index}`} className="data-admin-item-card">
                    <div className="data-admin-item-header">
                        <div className="data-admin-item-icon"><i className="fas fa-map-marked-alt"></i></div>
                        <div className="data-admin-item-actions">
                            <button className="data-admin-action-btn data-admin-info-btn" title="Lihat Penggunaan" onClick={() => openModal('usageIGT', igt)}><i className="fas fa-info-circle"></i></button>
                            <button className="data-admin-action-btn data-admin-edit-btn" title="Edit IGT" onClick={() => openModal('editIGT', igt)}><i className="fas fa-pencil-alt"></i></button>
                            <button className="data-admin-action-btn data-admin-delete-btn" title="Hapus IGT" onClick={() => openModal('deleteIGT', igt)}><i className="fas fa-trash-alt"></i></button>
                        </div>
                    </div>
                    <div className="data-admin-item-content">
                        <h4>{igt.nama_igt}</h4>
                        <p className="data-admin-item-code">Kode IGT: {igt.kode_igt}</p>
                        <p className="data-admin-item-kl">KL: {igt.nama_kl || igt.kode_kl}</p>
                        <span className={`data-admin-badge ${igt.jenis_ksp === 'Renaksi PKSP' ? 'data-admin-badge-primary' : 'data-admin-badge-secondary'}`}>
                            {igt.jenis_ksp}
                        </span>
                    </div>
                </div>
            ))}
        </div>
    );

    const renderIGTAsTable = () => (
        <div className="data-admin-table-container">
            <table className="data-admin-table" style={{ tableLayout: 'fixed', width: '100%' }}>
                <thead>
                    <tr>
                        <th style={{ width: '50px' }}>No</th>
                        <th style={{ width: '25%' }} onClick={() => requestSortIGT('nama_igt')} className={`sortable-header ${sortConfigIGT.key === 'nama_igt' ? 'active' : ''}`}>
                            Nama IGT {getSortIcon(sortConfigIGT, 'nama_igt')}
                        </th>
                        <th style={{ width: '18%' }} onClick={() => requestSortIGT('nama_kl')} className={`sortable-header ${sortConfigIGT.key === 'nama_kl' ? 'active' : ''}`}>
                            K/L {getSortIcon(sortConfigIGT, 'nama_kl')}
                        </th>
                        <th style={{ width: '100px' }} onClick={() => requestSortIGT('jenis_ksp')} className={`sortable-header ${sortConfigIGT.key === 'jenis_ksp' ? 'active' : ''}`}>
                            Jenis KSP {getSortIcon(sortConfigIGT, 'jenis_ksp')}
                        </th>
                        <th style={{ width: '110px' }} onClick={() => requestSortIGT('jenis_sk')} className={`sortable-header ${sortConfigIGT.key === 'jenis_sk' ? 'active' : ''}`}>
                            Renaksi/Walidata {getSortIcon(sortConfigIGT, 'jenis_sk')}
                        </th>
                        <th style={{ width: '100px' }}>Target</th>
                        <th style={{ width: '80px' }}>Satuan</th>
                        <th style={{ width: '100px' }} className="data-admin-table-actions-header">Aksi</th>
                    </tr>
                </thead>
                <tbody>
                    {currentIGT.map((igt, index) => (
                        <tr key={`igt-row-${igt.kode_kl}-${igt.kode_igt}-${index}`}>
                            <td>{(currentPageIGT - 1) * itemsPerPage + index + 1}</td>
                            <td style={{ wordWrap: 'break-word', overflow: 'hidden' }}>
                                <div>
                                    <strong>{igt.nama_igt}</strong>
                                    <div style={{ fontSize: '11px', color: '#718096' }}>Kode: {igt.kode_igt}</div>
                                </div>
                            </td>
                            <td style={{ wordWrap: 'break-word', overflow: 'hidden', fontSize: '13px' }}>{igt.nama_kl || igt.kode_kl}</td>
                            <td>
                                <span className={`data-admin-badge ${igt.jenis_ksp === 'Renaksi PKSP' ? 'data-admin-badge-primary' : 'data-admin-badge-secondary'}`}>
                                    {igt.jenis_ksp || '-'}
                                </span>
                            </td>
                            <td>
                                {igt.jenis_sk ? (
                                    <span className={`data-admin-badge ${
                                        igt.jenis_sk === 'Renaksi' ? 'data-admin-badge-warning' : 
                                        igt.jenis_sk === 'Walidata' ? 'data-admin-badge-info' : 
                                        igt.jenis_sk === 'Renaksi dan Walidata' ? 'data-admin-badge-success' : 
                                        'data-admin-badge-secondary'
                                    }`} style={{ 
                                        fontSize: '11px', 
                                        whiteSpace: 'nowrap',
                                        padding: '4px 8px'
                                    }}>
                                        {igt.jenis_sk}
                                    </span>
                                ) : (
                                    <span style={{ color: '#a0aec0', fontSize: '12px' }}>-</span>
                                )}
                            </td>
                            <td style={{ fontSize: '12px' }}>
                                {igt.target && Object.keys(igt.target).length > 0 ? (
                                    <div>
                                        {Object.entries(igt.target).map(([year, value]) => (
                                            <div key={year}>{year}: {value}</div>
                                        ))}
                                    </div>
                                ) : '-'}
                            </td>
                            <td style={{ fontSize: '12px' }}>{igt.satuan || '-'}</td>
                            <td>
                                <div className="data-admin-item-actions">
                                    <button className="data-admin-action-btn data-admin-info-btn" title="Lihat Detail" onClick={() => openModal('usageIGT', igt)}><i className="fas fa-info-circle"></i></button>
                                    <button className="data-admin-action-btn data-admin-edit-btn" title="Edit IGT" onClick={() => openModal('editIGT', igt)}><i className="fas fa-pencil-alt"></i></button>
                                    <button className="data-admin-action-btn data-admin-delete-btn" title="Hapus IGT" onClick={() => openModal('deleteIGT', igt)}><i className="fas fa-trash-alt"></i></button>
                                </div>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );

    const renderDataIGTTab = () => (
        <>
            <div className="data-admin-controls">
                <div className="data-admin-search-container">
                    <div className="data-admin-search-input-group">
                        <i className="fas fa-search data-admin-search-icon"></i>
                        <input
                            type="text"
                            placeholder="Cari berdasarkan Nama IGT, Kode IGT, atau Nama KL..."
                            className="data-admin-search-input"
                            value={searchTermIGT}
                            onChange={(e) => { setSearchTermIGT(e.target.value); setCurrentPageIGT(1); }}
                        />
                        {searchTermIGT && <button className="data-admin-search-clear" onClick={() => setSearchTermIGT('')}><i className="fas fa-times"></i></button>}
                    </div>
                    <div className="data-admin-filter-group" style={{ 
                        display: 'flex', 
                        gap: '12px', 
                        marginTop: '12px',
                        flexWrap: 'wrap',
                        width: '100%'
                    }}>
                        <select 
                            className="form-select" 
                            value={filterJenisKSP} 
                            onChange={(e) => { setFilterJenisKSP(e.target.value); setCurrentPageIGT(1); }}
                            style={{ minWidth: '200px', flex: '1' }}
                        >
                            <option value="">Semua Jenis KSP</option>
                            <option value="Renaksi PKSP">Renaksi PKSP</option>
                            <option value="Non Renaksi PKSP">Non Renaksi PKSP</option>
                        </select>
                        <select 
                            className="form-select" 
                            value={filterRenaksiWalidata} 
                            onChange={(e) => { setFilterRenaksiWalidata(e.target.value); setCurrentPageIGT(1); }}
                            style={{ minWidth: '200px', flex: '1' }}
                        >
                            <option value="">Semua Renaksi/Walidata</option>
                            <option value="Renaksi">Renaksi</option>
                            <option value="Walidata">Walidata</option>
                            <option value="Renaksi dan Walidata">Renaksi dan Walidata</option>
                        </select>
                        <select 
                            className="form-select" 
                            value={filterKL} 
                            onChange={(e) => { setFilterKL(e.target.value); setCurrentPageIGT(1); }}
                            style={{ minWidth: '250px', flex: '1' }}
                        >
                            <option value="">Semua K/L</option>
                            {klData.map(kl => (
                                <option key={kl.kode_kl} value={kl.kode_kl}>{kl.nama_kl}</option>
                            ))}
                        </select>
                        {(filterJenisKSP || filterKL || filterRenaksiWalidata) && (
                            <button 
                                className="btn btn-sm btn-outline-secondary" 
                                onClick={() => { setFilterJenisKSP(''); setFilterKL(''); setFilterRenaksiWalidata(''); setCurrentPageIGT(1); }}
                                title="Reset Filter"
                                style={{ whiteSpace: 'nowrap' }}
                            >
                                <i className="fas fa-times"></i> Reset
                            </button>
                        )}
                    </div>
                </div>
                {renderViewSwitcher()}
            </div>

            {currentIGT.length > 0 ? (
                viewMode === 'tabel' ? renderIGTAsTable() : renderIGTAsCards()
            ) : (
                <NoResultsFound term={searchTermIGT} type="Data IGT" />
            )}

            {renderPagination(currentPageIGT, totalPagesIGT, setCurrentPageIGT)}
        </>
    );

    // --- CRUD Checklist ---
    const handleAddChecklist = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const response = await axios.post(`${config.API_URL}/api/checklist`, formData, { headers: getAuthHeaders() });
            showSuccessMessage(response.data.message);
            fetchChecklist();
            closeModal();
        } catch (error) {
            handleApiError(error, 'Gagal menambah item checklist');
        } finally {
            setLoading(false);
        }
    };

    const handleEditChecklist = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const response = await axios.put(`${config.API_URL}/api/checklist/${currentItem.id}`, formData, { headers: getAuthHeaders() });
            showSuccessMessage(response.data.message);
            fetchChecklist();
            closeModal();
        } catch (error) {
            handleApiError(error, 'Gagal mengedit item checklist');
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteChecklist = async () => {
        setLoading(true);
        try {
            const response = await axios.delete(`${config.API_URL}/api/checklist/${currentItem.id}`, { headers: getAuthHeaders() });
            showSuccessMessage(response.data.message);
            fetchChecklist();
            closeModal();
        } catch (error) {
            handleApiError(error, 'Gagal menghapus item checklist');
        } finally {
            setLoading(false);
        }
    };

    const handleAddKepka = async (e) => {
        e.preventDefault();
        setLoading(true);

        const capaianObj = {};
        const targetObj = {};

        capaianYears.forEach(year => {
            const value = formData[`capaian_${year}`] || 0;
            capaianObj[year] = parseFloat(value) || 0;
        });

        targetYears.forEach(year => {
            const value = formData[`target_${year}`] || 0;
            targetObj[year] = parseFloat(value) || 0;
        });

        const payload = {
            no: formData.no,
            kode_igt: formData.kode_igt,
            jenis: formData.jenis,
            nama_igt_renaksi: formData.nama_igt_renaksi,
            nama_igt_walidata: formData.nama_igt_walidata || null,
            capaian: capaianObj,
            target: targetObj,
            satuan: formData.satuan
        };

        try {
            const response = await axios.post(`${config.API_URL}/api/management/kepka`, payload, { headers: getAuthHeaders() });
            showSuccessMessage(response.data.message);
            fetchKepka();
            closeModal();
        } catch (error) {
            handleApiError(error, 'Gagal menambah data kepka');
        } finally {
            setLoading(false);
        }
    };

    const handleEditKepka = async (e) => {
        e.preventDefault();
        setLoading(true);

        const capaianObj = {};
        const targetObj = {};

        capaianYears.forEach(year => {
            const value = formData[`capaian_${year}`] || formData.capaian?.[year] || 0;
            capaianObj[year] = parseFloat(value) || 0;
        });

        targetYears.forEach(year => {
            const value = formData[`target_${year}`] || formData.target?.[year] || 0;
            targetObj[year] = parseFloat(value) || 0;
        });

        const payload = {
            no: formData.no,
            kode_igt: formData.kode_igt,
            jenis: formData.jenis,
            nama_igt_renaksi: formData.nama_igt_renaksi,
            nama_igt_walidata: formData.nama_igt_walidata || null,
            capaian: capaianObj,
            target: targetObj,
            satuan: formData.satuan
        };

        try {
            const response = await axios.put(`${config.API_URL}/api/management/kepka/${currentItem.id}`, payload, { headers: getAuthHeaders() });
            showSuccessMessage(response.data.message);
            fetchKepka();
            closeModal();
        } catch (error) {
            handleApiError(error, 'Gagal mengedit data kepka');
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteKepka = async () => {
        setLoading(true);
        try {
            const response = await axios.delete(`${config.API_URL}/api/management/kepka/${currentItem.id}`, { headers: getAuthHeaders() });
            showSuccessMessage(response.data.message);
            fetchKepka();
            closeModal();
        } catch (error) {
            handleApiError(error, 'Gagal menghapus data kepka');
        } finally {
            setLoading(false);
        }
    };

    // Render Checklist Tab
    const renderChecklistTab = () => (
        <>
            <div className="data-admin-controls">
                <div className="data-admin-search-container">
                    <div className="data-admin-search-input-group">
                        <i className="fas fa-search data-admin-search-icon"></i>
                        <input
                            type="text"
                            placeholder="Cari berdasarkan dokumen atau deskripsi..."
                            className="data-admin-search-input"
                            value={searchTermChecklist}
                            onChange={(e) => { setSearchTermChecklist(e.target.value); setCurrentPageChecklist(1); }}
                        />
                        {searchTermChecklist && <button className="data-admin-search-clear" onClick={() => setSearchTermChecklist('')}><i className="fas fa-times"></i></button>}
                    </div>
                    <div className="data-admin-info-banner" style={{ marginTop: '12px' }}>
                        <i className="fas fa-info-circle"></i>
                        <span>Kelola item checklist untuk pengecekan dokumen. Setiap dokumen dapat memiliki checklist yang berbeda.</span>
                    </div>
                </div>
            </div>

            {filteredChecklist.length > 0 ? (
                <div className="data-admin-table-container">
                    <table className="data-admin-table" style={{ tableLayout: 'fixed', width: '100%' }}>
                        <thead>
                            <tr>
                                <th style={{ width: '50px' }}>No</th>
                                <th style={{ width: '120px' }}>Dokumen</th>
                                <th style={{ width: '100px' }}>PRIORITAS</th>
                                <th style={{ width: 'auto' }}>Deskripsi</th>
                                <th style={{ width: '150px' }}>Hint Template</th>
                                <th style={{ width: '80px' }}>Status</th>
                                <th style={{ width: '100px' }} className="data-admin-table-actions-header">Aksi</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredChecklist.map((item, index) => (
                                <tr key={`checklist-${item.id}-${index}`}>
                                    <td>{index + 1}</td>
                                    <td><span className="data-admin-table-code">{item.document_type}</span></td>
                                    <td>
                                        <span className={`priority-badge ${item.priority === 'mandatory' ? 'priority-mandatory' : 'priority-optional'}`}>
                                            {item.priority === 'mandatory' ? 'Wajib' : 'Opsional'}
                                        </span>
                                    </td>
                                    <td><span className="data-admin-table-name">{item.description}</span></td>
                                    <td>
                                        <span style={{ fontSize: '13px', color: '#64748b', fontStyle: item.input_hint ? 'normal' : 'italic' }}>
                                            {item.input_hint || 'Tidak ada hint'}
                                        </span>
                                    </td>
                                    <td>
                                        <span className={`status-badge ${item.is_active ? 'status-active' : 'status-inactive'}`}>
                                            {item.is_active ? 'Aktif' : 'Nonaktif'}
                                        </span>
                                    </td>
                                    <td>
                                        <div className="data-admin-item-actions">
                                            <button className="data-admin-action-btn data-admin-edit-btn" title="Edit Checklist" onClick={() => openModal('editChecklist', item)}>
                                                <i className="fas fa-pencil-alt"></i>
                                            </button>
                                            <button className="data-admin-action-btn data-admin-delete-btn" title="Hapus Checklist" onClick={() => openModal('deleteChecklist', item)}>
                                                <i className="fas fa-trash-alt"></i>
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            ) : (
                <NoResultsFound term="" type="item checklist" />
            )}
        </>
    );

    const renderKepkaTab = () => (
        <>
            <div className="data-admin-controls">
                <div className="data-admin-search-container">
                    <div className="data-admin-search-input-group">
                        <i className="fas fa-search data-admin-search-icon"></i>
                        <input
                            type="text"
                            placeholder="Cari berdasarkan No, Kode IGT, Jenis, atau Nama IGT..."
                            className="data-admin-search-input"
                            value={searchTermKepka}
                            onChange={(e) => { setSearchTermKepka(e.target.value); setCurrentPageKepka(1); }}
                        />
                        {searchTermKepka && <button className="data-admin-search-clear" onClick={() => setSearchTermKepka('')}><i className="fas fa-times"></i></button>}
                    </div>
                </div>
            </div>

            {currentKepka.length > 0 ? (
                <div className="data-admin-table-container">
                    <table className="data-admin-table">
                        <thead>
                            <tr>
                                <th style={{width: '50px'}}>No</th>
                                <th onClick={() => requestSortKepka('no')} className={`sortable-header ${sortConfigKepka.key === 'no' ? 'active' : ''}`}>
                                    Nomor Kepka {getSortIcon(sortConfigKepka, 'no')}
                                </th>
                                <th onClick={() => requestSortKepka('nama_kl')} className={`sortable-header ${sortConfigKepka.key === 'nama_kl' ? 'active' : ''}`}>
                                    K/L {getSortIcon(sortConfigKepka, 'nama_kl')}
                                </th>
                                <th onClick={() => requestSortKepka('kode_igt')} className={`sortable-header ${sortConfigKepka.key === 'kode_igt' ? 'active' : ''}`}>
                                    Kode IGT {getSortIcon(sortConfigKepka, 'kode_igt')}
                                </th>
                                <th onClick={() => requestSortKepka('jenis')} className={`sortable-header ${sortConfigKepka.key === 'jenis' ? 'active' : ''}`}>
                                    Jenis SK {getSortIcon(sortConfigKepka, 'jenis')}
                                </th>
                                <th onClick={() => requestSortKepka('nama_igt_walidata')} className={`sortable-header ${sortConfigKepka.key === 'nama_igt_walidata' ? 'active' : ''}`}>
                                    Nama IGT Walidata {getSortIcon(sortConfigKepka, 'nama_igt_walidata')}
                                </th>
                                <th>Nama IGT Renaksi</th>
                                <th>Target</th>
                                <th>Satuan</th>
                                <th className="data-admin-table-actions-header">Aksi</th>
                            </tr>
                        </thead>
                        <tbody>
                            {currentKepka.map((kepka, index) => (
                                <tr key={`kepka-${kepka.id}-${index}`}>
                                    <td>{(currentPageKepka - 1) * itemsPerPage + index + 1}</td>
                                    <td><span className="data-admin-table-code">{kepka.no}</span></td>
                                    <td>{kepka.nama_kl || '-'}</td>
                                    <td><span className="data-admin-table-code">{kepka.kode_igt}</span></td>
                                    <td>
                                        <span className={`data-admin-badge ${kepka.jenis === 'Renaksi' ? 'data-admin-badge-warning' :
                                            kepka.jenis === 'Walidata' ? 'data-admin-badge-info' :
                                                'data-admin-badge-success'
                                            }`}>
                                            {kepka.jenis}
                                        </span>
                                    </td>
                                    <td>{kepka.nama_igt_walidata}</td>
                                    <td>{kepka.nama_igt_renaksi || '-'}</td>
                                    <td>
                                        {kepka.target && Object.keys(kepka.target).length > 0 ? (
                                            <div style={{ fontSize: '12px' }}>
                                                {Object.entries(kepka.target).map(([year, value]) => (
                                                    <div key={year}>{year}: {value}</div>
                                                ))}
                                            </div>
                                        ) : '-'}
                                    </td>
                                    <td>{kepka.satuan || '-'}</td>
                                    <td>
                                        <div className="data-admin-item-actions">
                                            <button className="data-admin-action-btn data-admin-edit-btn" title="Edit Kepka" onClick={() => openModal('editKepka', kepka)}>
                                                <i className="fas fa-pencil-alt"></i>
                                            </button>
                                            <button className="data-admin-action-btn data-admin-delete-btn" title="Hapus Kepka" onClick={() => openModal('deleteKepka', kepka)}>
                                                <i className="fas fa-trash-alt"></i>
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            ) : (
                <NoResultsFound term={searchTermKepka} type="data kepka" />
            )}

            {renderPagination(currentPageKepka, totalPagesKepka, setCurrentPageKepka)}
        </>
    );

    const renderModal = () => {
        if (!showModal) return null;

        const isAdd = showModal.includes('add');
        const isEdit = showModal.includes('edit');
        const isDelete = showModal.includes('delete');
        const isUsage = showModal.includes('usage');
        const isKL = showModal.includes('KL');
        const isIGT = showModal.includes('IGT');
        const isChecklist = showModal.includes('Checklist');
        const isKepka = showModal.includes('Kepka');

        if (isAdd || isEdit) {
            const title = `${isAdd ? 'Tambah' : 'Edit'} ${isKL ? 'Kementerian/Lembaga' : isChecklist ? 'Item Checklist' : isKepka ? 'Data Kepka' : 'Data IGT'}`;
            const handleSubmit = isKL ? (isAdd ? handleAddKL : handleEditKL) :
                isChecklist ? (isAdd ? handleAddChecklist : handleEditChecklist) :
                    isKepka ? (isAdd ? handleAddKepka : handleEditKepka) :
                        (isAdd ? handleAddIGT : handleEditIGT);

            return (
                <div className="data-admin-modal-overlay" onClick={closeModal}>
                    <div className="data-admin-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="data-admin-modal-header">
                            <h3><i className={`fas ${isKL ? 'fa-building' : 'fa-map-marked-alt'}`}></i> {title}</h3>
                            <button className="data-admin-modal-close" onClick={closeModal} disabled={loading}><i className="fas fa-times"></i></button>
                        </div>
                        <form onSubmit={handleSubmit}>
                            <div className="data-admin-modal-body">
                                <div className="data-admin-form-container">
                                    {isKL ? (
                                        <>
                                            <div className="data-admin-input-group">
                                                <label>Kode KL <span className="data-admin-required">*</span></label>
                                                <input type="text" name="kode_kl" value={formData.kode_kl || ''} onChange={handleInputChange} required disabled={isEdit || loading} className={isEdit ? 'data-admin-input-disabled' : ''} />
                                                <div className="data-admin-input-hint">Kode unik untuk KL. Tidak dapat diubah setelah dibuat.</div>
                                            </div>
                                            <div className="data-admin-input-group">
                                                <label>Nama KL <span className="data-admin-required">*</span></label>
                                                <input type="text" name="nama_kl" value={formData.nama_kl || ''} onChange={handleInputChange} required disabled={loading} />
                                            </div>
                                        </>
                                    ) : isKepka ? ( // Form Kepka
                                        <>
                                            <div className="data-admin-input-group">
                                                <label>Nomor Kepka <span className="data-admin-required">*</span></label>
                                                <input 
                                                    type="text" 
                                                    name="no" 
                                                    value={formData.no || ''} 
                                                    onChange={handleInputChange} 
                                                    required 
                                                    disabled={loading}
                                                    placeholder="Contoh: KEPKA 130/2025"
                                                />
                                                <div className="data-admin-input-hint">Format: KEPKA XXX/YYYY (bisa sama untuk beberapa data)</div>
                                            </div>
                                            <div className="data-admin-input-group">
                                                <label>Nama IGT Walidata <span className="data-admin-required">*</span></label>
                                                <select 
                                                    name="nama_igt_walidata" 
                                                    value={formData.nama_igt_walidata || ''} 
                                                    onChange={(e) => {
                                                        const selectedIgt = dataIgt.find(igt => igt.nama_igt === e.target.value);
                                                        setFormData(prev => ({
                                                            ...prev,
                                                            nama_igt_walidata: e.target.value,
                                                            kode_igt: selectedIgt ? selectedIgt.kode_igt : ''
                                                        }));
                                                    }} 
                                                    required 
                                                    disabled={loading}
                                                >
                                                    <option value="">Pilih Nama IGT</option>
                                                    {dataIgt.map((igt, index) => (
                                                        <option key={`kepka-igt-${igt.kode_igt}-${index}`} value={igt.nama_igt}>
                                                            {igt.nama_igt}
                                                        </option>
                                                    ))}
                                                </select>
                                                <div className="data-admin-input-hint">Pilih nama IGT (kode IGT akan otomatis terisi)</div>
                                            </div>
                                            <div className="data-admin-input-group">
                                                <label>Jenis SK <span className="data-admin-required">*</span></label>
                                                <select name="jenis" value={formData.jenis || ''} onChange={handleInputChange} required disabled={loading}>
                                                    <option value="">Pilih Jenis SK</option>
                                                    <option value="Renaksi">Renaksi</option>
                                                    <option value="Walidata">Walidata</option>
                                                    <option value="Renaksi dan Walidata">Renaksi dan Walidata</option>
                                                </select>
                                            </div>
                                            <div className="data-admin-input-group">
                                                <label>Nama IGT Renaksi</label>
                                                <input type="text" name="nama_igt_renaksi" value={formData.nama_igt_renaksi || ''} onChange={handleInputChange} disabled={loading} placeholder="Isi manual jika diperlukan" />
                                                <div className="data-admin-input-hint">Opsional - Isi manual sesuai kebutuhan</div>
                                            </div>
                                            <div className="data-admin-input-group">
                                                <label>Satuan <span className="data-admin-required">*</span></label>
                                                <input type="text" name="satuan" value={formData.satuan || ''} onChange={handleInputChange} required disabled={loading} placeholder="Contoh: Hektar, Rekomendasi, Lembar Peta" />
                                            </div>

                                            {/* Target Multi-Tahun */}
                                            <div className="data-admin-input-group">
                                                <label>Target per Tahun <span className="data-admin-required">*</span></label>
                                                {targetYears.map((year, index) => (
                                                    <div key={`target-${year}-${index}`} style={{ display: 'flex', gap: '10px', marginBottom: '8px', alignItems: 'center' }}>
                                                        <select
                                                            value={year}
                                                            onChange={(e) => {
                                                                const newYears = [...targetYears];
                                                                newYears[index] = e.target.value;
                                                                setTargetYears(newYears);
                                                            }}
                                                            disabled={loading}
                                                            style={{ flex: '0 0 120px' }}
                                                        >
                                                            {Array.from({ length: 10 }, (_, i) => new Date().getFullYear() - 2 + i).map(y => (
                                                                <option key={y} value={y}>{y}</option>
                                                            ))}
                                                        </select>
                                                        <input
                                                            type="text"
                                                            name={`target_${year}`}
                                                            value={formData[`target_${year}`] || formData.target?.[year] || ''}
                                                            onChange={handleInputChange}
                                                            placeholder="Nilai target (gunakan titik untuk desimal)"
                                                            required
                                                            disabled={loading}
                                                            style={{ flex: 1 }}
                                                        />
                                                        {targetYears.length > 1 && (
                                                            <button
                                                                type="button"
                                                                onClick={() => setTargetYears(targetYears.filter((_, i) => i !== index))}
                                                                disabled={loading}
                                                                style={{ padding: '6px 10px', background: '#ef4444', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                                                            >
                                                                <i className="fas fa-times"></i>
                                                            </button>
                                                        )}
                                                    </div>
                                                ))}
                                                <button
                                                    type="button"
                                                    onClick={() => setTargetYears([...targetYears, new Date().getFullYear()])}
                                                    disabled={loading}
                                                    style={{ marginTop: '8px', padding: '8px 12px', background: '#3b82f6', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '13px' }}
                                                >
                                                    <i className="fas fa-plus"></i> Tambah Tahun
                                                </button>
                                            </div>

                                            {/* Capaian Multi-Tahun (Opsional untuk edit) */}
                                            {isEdit && (
                                                <div className="data-admin-input-group">
                                                    <label>Capaian per Tahun (Opsional)</label>
                                                    {capaianYears.map((year, index) => (
                                                        <div key={`capaian-${year}-${index}`} style={{ display: 'flex', gap: '10px', marginBottom: '8px', alignItems: 'center' }}>
                                                            <select
                                                                value={year}
                                                                onChange={(e) => {
                                                                    const newYears = [...capaianYears];
                                                                    newYears[index] = e.target.value;
                                                                    setCapaianYears(newYears);
                                                                }}
                                                                disabled={loading}
                                                                style={{ flex: '0 0 120px' }}
                                                            >
                                                                {Array.from({ length: 10 }, (_, i) => new Date().getFullYear() - 2 + i).map(y => (
                                                                    <option key={y} value={y}>{y}</option>
                                                                ))}
                                                            </select>
                                                            <input
                                                                type="text"
                                                                name={`capaian_${year}`}
                                                                value={formData[`capaian_${year}`] || formData.capaian?.[year] || ''}
                                                                onChange={handleInputChange}
                                                                placeholder="Nilai capaian (gunakan titik untuk desimal)"
                                                                disabled={loading}
                                                                style={{ flex: 1 }}
                                                            />
                                                            {capaianYears.length > 1 && (
                                                                <button
                                                                    type="button"
                                                                    onClick={() => setCapaianYears(capaianYears.filter((_, i) => i !== index))}
                                                                    disabled={loading}
                                                                    style={{ padding: '6px 10px', background: '#ef4444', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                                                                >
                                                                    <i className="fas fa-times"></i>
                                                                </button>
                                                            )}
                                                        </div>
                                                    ))}
                                                    <button
                                                        type="button"
                                                        onClick={() => setCapaianYears([...capaianYears, new Date().getFullYear()])}
                                                        disabled={loading}
                                                        style={{ marginTop: '8px', padding: '8px 12px', background: '#3b82f6', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '13px' }}
                                                    >
                                                        <i className="fas fa-plus"></i> Tambah Tahun
                                                    </button>
                                                    <div className="data-admin-input-hint">Capaian akan diupdate otomatis saat user melakukan upload</div>
                                                </div>
                                            )}
                                        </>
                                    ) : isChecklist ? ( // Form Checklist
                                        <>
                                            <div className="data-admin-input-group">
                                                <label>Dokumen <span className="data-admin-required">*</span></label>
                                                <select name="document_type" value={formData.document_type || ''} onChange={handleInputChange} required disabled={loading}>
                                                    <option value="">Pilih Dokumen</option>
                                                    <option value="peta">Peta</option>
                                                    <option value="struktur_data">Struktur Data</option>
                                                    <option value="takel">Mekanisme dan Tata Kerja</option>
                                                    <option value="dokumen">Dokumen Penetapan</option>
                                                    <option value="metadata">Metadata</option>
                                                    <option value="simbology">Simbology</option>
                                                    <option value="role">Role Akses</option>
                                                    <option value="kualitas">Kontrol Kualitas</option>
                                                    <option value="cakupan">Cakupan</option>
                                                </select>
                                            </div>
                                            <div className="data-admin-input-group">
                                                <label>Prioritas <span className="data-admin-required">*</span></label>
                                                <select name="priority" value={formData.priority || 'mandatory'} onChange={handleInputChange} disabled={loading}>
                                                    <option value="mandatory">Wajib</option>
                                                    <option value="optional">Opsional</option>
                                                </select>
                                                <div className="data-admin-input-hint">User tidak akan melihat status wajib/opsional</div>
                                            </div>
                                            <div className="data-admin-input-group">
                                                <label>Deskripsi <span className="data-admin-required">*</span></label>
                                                <textarea name="description" value={formData.description || ''} onChange={handleInputChange} required disabled={loading} rows="3" placeholder="Deskripsi item checklist"></textarea>
                                                <div className="data-admin-input-hint">Deskripsi yang akan ditampilkan sebagai label input</div>
                                            </div>
                                            <div className="data-admin-input-group">
                                                <label>Hint Template Input</label>
                                                <input 
                                                    type="text" 
                                                    name="input_hint" 
                                                    value={formData.input_hint || ''} 
                                                    onChange={handleInputChange} 
                                                    disabled={loading} 
                                                    placeholder="Contoh: Nomor SK 123/2024 atau Format: DD/MM/YYYY"
                                                />
                                                <div className="data-admin-input-hint">Template/contoh untuk membantu user mengisi input (opsional)</div>
                                            </div>
                                            <div className="data-admin-input-group">
                                                <label>Status</label>
                                                <select name="is_active" value={formData.is_active !== undefined ? formData.is_active : true} onChange={handleInputChange} disabled={loading}>
                                                    <option value={true}>Aktif</option>
                                                    <option value={false}>Nonaktif</option>
                                                </select>
                                            </div>
                                        </>
                                    ) : (
                                        <>
                                            {/* Kelompok IGT Selector */}
                                            <div className="data-admin-input-group">
                                                <label>Kelompok IGT <span className="data-admin-required">*</span></label>
                                                <select 
                                                    name="jenis_sk" 
                                                    value={formData.jenis_sk || ''} 
                                                    onChange={(e) => {
                                                        const value = e.target.value;
                                                        setFormData(prev => ({
                                                            ...prev,
                                                            jenis_sk: value,
                                                            // Reset related fields based on selection
                                                            selected_igt: '',
                                                            nama_igt_walidata: '',
                                                            nama_igt_renaksi: '',
                                                            kode_igt: isEdit ? prev.kode_igt : '',
                                                            kode_kl: isEdit ? prev.kode_kl : ''
                                                        }));
                                                    }} 
                                                    required 
                                                    disabled={loading}
                                                >
                                                    <option value="">Pilih Kelompok IGT</option>
                                                    <option value="Renaksi">Renaksi</option>
                                                    <option value="Walidata">Walidata</option>
                                                    <option value="Renaksi dan Walidata">Renaksi dan Walidata</option>
                                                    <option value="Lainnya">Lainnya</option>
                                                </select>
                                            </div>

                                            {/* === FORM: Renaksi === */}
                                            {formData.jenis_sk === 'Renaksi' && (
                                                <>
                                                    {isAdd && (
                                                        <div style={{ background: '#fef3c7', border: '1px solid #fcd34d', borderRadius: '8px', padding: '12px', marginBottom: '16px' }}>
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#92400e' }}>
                                                                <i className="fas fa-info-circle"></i>
                                                                <span style={{ fontSize: '13px' }}>Data IGT baru akan dibuat otomatis berdasarkan Nama IGT Renaksi</span>
                                                            </div>
                                                        </div>
                                                    )}
                                                    <div className="data-admin-input-group">
                                                        <label>Kementerian/Lembaga <span className="data-admin-required">*</span></label>
                                                        <select name="kode_kl" value={formData.kode_kl || ''} onChange={handleInputChange} required disabled={loading}>
                                                            <option value="">Pilih KL</option>
                                                            {klData.map(kl => (
                                                                <option key={kl.kode_kl} value={kl.kode_kl}>{kl.nama_kl}</option>
                                                            ))}
                                                        </select>
                                                    </div>
                                                    <div className="data-admin-input-group">
                                                        <label>Kode IGT <span className="data-admin-required">*</span></label>
                                                        <input type="text" name="kode_igt" value={formData.kode_igt || ''} onChange={handleInputChange} required disabled={loading} placeholder="Contoh: 032084" />
                                                    </div>
                                                    <div className="data-admin-input-group">
                                                        <label>Nama IGT Renaksi <span className="data-admin-required">*</span></label>
                                                        <input type="text" name="nama_igt_renaksi" value={formData.nama_igt_renaksi || ''} onChange={handleInputChange} required disabled={loading} placeholder="Masukkan Nama IGT sesuai Renaksi" />
                                                        <div className="data-admin-input-hint">Nama ini akan menjadi nama IGT di tabel master</div>
                                                    </div>
                                                    <div className="data-admin-input-group">
                                                        <label>Jenis KSP <span className="data-admin-required">*</span></label>
                                                        <select name="jenis_ksp" value={formData.jenis_ksp || 'Renaksi PKSP'} onChange={handleInputChange} required disabled={loading}>
                                                            <option value="Renaksi PKSP">Renaksi PKSP</option>
                                                            <option value="Non Renaksi PKSP">Non Renaksi PKSP</option>
                                                        </select>
                                                    </div>
                                                    <div className="data-admin-input-group">
                                                        <label>Nomor Kepka</label>
                                                        <input type="text" name="nomor_kepka" value={formData.nomor_kepka || ''} onChange={handleInputChange} disabled={loading} placeholder="Contoh: KEPKA 130/2025" />
                                                    </div>
                                                    <div className="data-admin-input-group">
                                                        <label>Satuan <span className="data-admin-required">*</span></label>
                                                        <input type="text" name="satuan" value={formData.satuan || ''} onChange={handleInputChange} required disabled={loading} placeholder="Contoh: Hektar, Rekomendasi" />
                                                    </div>
                                                    <div className="data-admin-input-group">
                                                        <label>Target per Tahun <span className="data-admin-required">*</span></label>
                                                        {targetYears.map((year, index) => (
                                                            <div key={`target-${year}-${index}`} style={{ display: 'flex', gap: '10px', marginBottom: '8px', alignItems: 'center' }}>
                                                                <select value={year} onChange={(e) => { const newYears = [...targetYears]; newYears[index] = e.target.value; setTargetYears(newYears); }} disabled={loading} style={{ flex: '0 0 120px' }}>
                                                                    {Array.from({ length: 10 }, (_, i) => new Date().getFullYear() - 2 + i).map(y => (<option key={y} value={y}>{y}</option>))}
                                                                </select>
                                                                <input type="text" name={`target_${year}`} value={formData[`target_${year}`] || ''} onChange={handleInputChange} placeholder="Nilai target" disabled={loading} style={{ flex: 1 }} required />
                                                                {targetYears.length > 1 && (
                                                                    <button type="button" onClick={() => setTargetYears(targetYears.filter((_, i) => i !== index))} disabled={loading} style={{ padding: '6px 10px', background: '#ef4444', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
                                                                        <i className="fas fa-times"></i>
                                                                    </button>
                                                                )}
                                                            </div>
                                                        ))}
                                                        <button type="button" onClick={() => setTargetYears([...targetYears, new Date().getFullYear()])} disabled={loading} style={{ marginTop: '8px', padding: '8px 12px', background: '#3b82f6', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '13px' }}>
                                                            <i className="fas fa-plus"></i> Tambah Tahun
                                                        </button>
                                                    </div>
                                                </>
                                            )}

                                            {/* === FORM: Walidata === */}
                                            {formData.jenis_sk === 'Walidata' && (
                                                <>
                                                    {isAdd && (
                                                        <div style={{ background: '#eff6ff', border: '1px solid #93c5fd', borderRadius: '8px', padding: '12px', marginBottom: '16px' }}>
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#1e40af' }}>
                                                                <i className="fas fa-info-circle"></i>
                                                                <span style={{ fontSize: '13px' }}>Data IGT baru akan dibuat berdasarkan Nama IGT Walidata. Satuan dan Target bersifat opsional.</span>
                                                            </div>
                                                        </div>
                                                    )}
                                                    <div className="data-admin-input-group">
                                                        <label>Kementerian/Lembaga <span className="data-admin-required">*</span></label>
                                                        <select name="kode_kl" value={formData.kode_kl || ''} onChange={handleInputChange} required disabled={loading}>
                                                            <option value="">Pilih KL</option>
                                                            {klData.map(kl => (
                                                                <option key={kl.kode_kl} value={kl.kode_kl}>{kl.nama_kl}</option>
                                                            ))}
                                                        </select>
                                                    </div>
                                                    <div className="data-admin-input-group">
                                                        <label>Kode IGT <span className="data-admin-required">*</span></label>
                                                        <input type="text" name="kode_igt" value={formData.kode_igt || ''} onChange={handleInputChange} required disabled={loading} placeholder="Contoh: 032084" />
                                                    </div>
                                                    <div className="data-admin-input-group">
                                                        <label>Nama IGT Walidata <span className="data-admin-required">*</span></label>
                                                        <input type="text" name="nama_igt_walidata" value={formData.nama_igt_walidata || ''} onChange={handleInputChange} required disabled={loading} placeholder="Masukkan Nama IGT sesuai Walidata" />
                                                        <div className="data-admin-input-hint">Nama ini akan menjadi nama IGT di tabel master</div>
                                                    </div>
                                                    <div className="data-admin-input-group">
                                                        <label>Jenis KSP <span className="data-admin-required">*</span></label>
                                                        <select name="jenis_ksp" value={formData.jenis_ksp || ''} onChange={handleInputChange} required disabled={loading}>
                                                            <option value="">Pilih Jenis KSP</option>
                                                            <option value="Renaksi PKSP">Renaksi PKSP</option>
                                                            <option value="Non Renaksi PKSP">Non Renaksi PKSP</option>
                                                        </select>
                                                    </div>
                                                    <div className="data-admin-input-group">
                                                        <label>Nomor Kepka</label>
                                                        <input type="text" name="nomor_kepka" value={formData.nomor_kepka || ''} onChange={handleInputChange} disabled={loading} placeholder="Contoh: KEPKA 130/2025" />
                                                    </div>
                                                    <div className="data-admin-input-group">
                                                        <label>Satuan</label>
                                                        <input type="text" name="satuan" value={formData.satuan || ''} onChange={handleInputChange} disabled={loading} placeholder="Opsional - Contoh: Hektar, Rekomendasi" />
                                                    </div>
                                                    <div className="data-admin-input-group">
                                                        <label>Target per Tahun</label>
                                                        {targetYears.map((year, index) => (
                                                            <div key={`target-${year}-${index}`} style={{ display: 'flex', gap: '10px', marginBottom: '8px', alignItems: 'center' }}>
                                                                <select value={year} onChange={(e) => { const newYears = [...targetYears]; newYears[index] = e.target.value; setTargetYears(newYears); }} disabled={loading} style={{ flex: '0 0 120px' }}>
                                                                    {Array.from({ length: 10 }, (_, i) => new Date().getFullYear() - 2 + i).map(y => (<option key={y} value={y}>{y}</option>))}
                                                                </select>
                                                                <input type="text" name={`target_${year}`} value={formData[`target_${year}`] || ''} onChange={handleInputChange} placeholder="Opsional - Nilai target" disabled={loading} style={{ flex: 1 }} />
                                                                {targetYears.length > 1 && (
                                                                    <button type="button" onClick={() => setTargetYears(targetYears.filter((_, i) => i !== index))} disabled={loading} style={{ padding: '6px 10px', background: '#ef4444', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
                                                                        <i className="fas fa-times"></i>
                                                                    </button>
                                                                )}
                                                            </div>
                                                        ))}
                                                        <button type="button" onClick={() => setTargetYears([...targetYears, new Date().getFullYear()])} disabled={loading} style={{ marginTop: '8px', padding: '8px 12px', background: '#3b82f6', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '13px' }}>
                                                            <i className="fas fa-plus"></i> Tambah Tahun
                                                        </button>
                                                        <div className="data-admin-input-hint" style={{ marginTop: '8px' }}>Opsional untuk Walidata</div>
                                                    </div>
                                                </>
                                            )}

                                            {/* === FORM: Renaksi dan Walidata === */}
                                            {formData.jenis_sk === 'Renaksi dan Walidata' && (
                                                <>
                                                    {isAdd && (
                                                        <div style={{ background: '#f0fdf4', border: '1px solid #86efac', borderRadius: '8px', padding: '12px', marginBottom: '16px' }}>
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#166534' }}>
                                                                <i className="fas fa-info-circle"></i>
                                                                <span style={{ fontSize: '13px' }}>Data IGT akan dibuat berdasarkan Nama IGT Walidata. Nama IGT Renaksi bersifat opsional dan hanya masuk ke tabel Kepka.</span>
                                                            </div>
                                                        </div>
                                                    )}
                                                    <div className="data-admin-input-group">
                                                        <label>Kementerian/Lembaga <span className="data-admin-required">*</span></label>
                                                        <select name="kode_kl" value={formData.kode_kl || ''} onChange={handleInputChange} required disabled={loading}>
                                                            <option value="">Pilih KL</option>
                                                            {klData.map(kl => (
                                                                <option key={kl.kode_kl} value={kl.kode_kl}>{kl.nama_kl}</option>
                                                            ))}
                                                        </select>
                                                    </div>
                                                    <div className="data-admin-input-group">
                                                        <label>Kode IGT <span className="data-admin-required">*</span></label>
                                                        <input type="text" name="kode_igt" value={formData.kode_igt || ''} onChange={handleInputChange} required disabled={loading} placeholder="Contoh: 032084" />
                                                    </div>
                                                    <div className="data-admin-input-group">
                                                        <label>Nama IGT Walidata <span className="data-admin-required">*</span></label>
                                                        <input type="text" name="nama_igt_walidata" value={formData.nama_igt_walidata || ''} onChange={handleInputChange} required disabled={loading} placeholder="Masukkan Nama IGT sesuai Walidata" />
                                                        <div className="data-admin-input-hint">Nama ini akan menjadi nama IGT di tabel master</div>
                                                    </div>
                                                    <div className="data-admin-input-group">
                                                        <label>Nama IGT Renaksi</label>
                                                        <input type="text" name="nama_igt_renaksi" value={formData.nama_igt_renaksi || ''} onChange={handleInputChange} disabled={loading} placeholder="Opsional - jika berbeda dari Walidata" />
                                                        <div className="data-admin-input-hint">Opsional - hanya disimpan di tabel Kepka</div>
                                                    </div>
                                                    <div className="data-admin-input-group">
                                                        <label>Jenis KSP <span className="data-admin-required">*</span></label>
                                                        <select name="jenis_ksp" value={formData.jenis_ksp || ''} onChange={handleInputChange} required disabled={loading}>
                                                            <option value="">Pilih Jenis KSP</option>
                                                            <option value="Renaksi PKSP">Renaksi PKSP</option>
                                                            <option value="Non Renaksi PKSP">Non Renaksi PKSP</option>
                                                        </select>
                                                    </div>
                                                    <div className="data-admin-input-group">
                                                        <label>Nomor Kepka</label>
                                                        <input type="text" name="nomor_kepka" value={formData.nomor_kepka || ''} onChange={handleInputChange} disabled={loading} placeholder="Contoh: KEPKA 130/2025" />
                                                    </div>
                                                    <div className="data-admin-input-group">
                                                        <label>Satuan</label>
                                                        <input type="text" name="satuan" value={formData.satuan || ''} onChange={handleInputChange} disabled={loading} placeholder="Opsional - Contoh: Hektar, Rekomendasi" />
                                                    </div>
                                                    <div className="data-admin-input-group">
                                                        <label>Target per Tahun</label>
                                                        {targetYears.map((year, index) => (
                                                            <div key={`target-${year}-${index}`} style={{ display: 'flex', gap: '10px', marginBottom: '8px', alignItems: 'center' }}>
                                                                <select value={year} onChange={(e) => { const newYears = [...targetYears]; newYears[index] = e.target.value; setTargetYears(newYears); }} disabled={loading} style={{ flex: '0 0 120px' }}>
                                                                    {Array.from({ length: 10 }, (_, i) => new Date().getFullYear() - 2 + i).map(y => (<option key={y} value={y}>{y}</option>))}
                                                                </select>
                                                                <input type="text" name={`target_${year}`} value={formData[`target_${year}`] || ''} onChange={handleInputChange} placeholder="Opsional - Nilai target" disabled={loading} style={{ flex: 1 }} />
                                                                {targetYears.length > 1 && (
                                                                    <button type="button" onClick={() => setTargetYears(targetYears.filter((_, i) => i !== index))} disabled={loading} style={{ padding: '6px 10px', background: '#ef4444', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
                                                                        <i className="fas fa-times"></i>
                                                                    </button>
                                                                )}
                                                            </div>
                                                        ))}
                                                        <button type="button" onClick={() => setTargetYears([...targetYears, new Date().getFullYear()])} disabled={loading} style={{ marginTop: '8px', padding: '8px 12px', background: '#3b82f6', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '13px' }}>
                                                            <i className="fas fa-plus"></i> Tambah Tahun
                                                        </button>
                                                        <div className="data-admin-input-hint" style={{ marginTop: '8px' }}>Opsional untuk Walidata</div>
                                                    </div>
                                                </>
                                            )}

                                            {/* === FORM: Lainnya === */}
                                            {formData.jenis_sk === 'Lainnya' && (
                                                <>
                                                    <div className="data-admin-input-group">
                                                        <label>Kementerian/Lembaga <span className="data-admin-required">*</span></label>
                                                        <select name="kode_kl" value={formData.kode_kl || ''} onChange={handleInputChange} required disabled={loading}>
                                                            <option value="">Pilih KL</option>
                                                            {klData.map(kl => (
                                                                <option key={kl.kode_kl} value={kl.kode_kl}>{kl.nama_kl}</option>
                                                            ))}
                                                        </select>
                                                    </div>
                                                    <div className="data-admin-input-group">
                                                        <label>Kode IGT <span className="data-admin-required">*</span></label>
                                                        <input type="text" name="kode_igt" value={formData.kode_igt || ''} onChange={handleInputChange} required disabled={loading} placeholder="Contoh: 032084" />
                                                    </div>
                                                    <div className="data-admin-input-group">
                                                        <label>Nama IGT <span className="data-admin-required">*</span></label>
                                                        <input type="text" name="nama_igt" value={formData.nama_igt || ''} onChange={handleInputChange} required disabled={loading} placeholder="Masukkan Nama IGT" />
                                                    </div>
                                                    <div className="data-admin-input-group">
                                                        <label>Jenis KSP <span className="data-admin-required">*</span></label>
                                                        <select name="jenis_ksp" value={formData.jenis_ksp || ''} onChange={handleInputChange} required disabled={loading}>
                                                            <option value="">Pilih Jenis KSP</option>
                                                            <option value="Renaksi PKSP">Renaksi PKSP</option>
                                                            <option value="Non Renaksi PKSP">Non Renaksi PKSP</option>
                                                        </select>
                                                    </div>
                                                </>
                                            )}
                                        </>
                                    )}
                                </div>
                            </div>
                            <div className="data-admin-modal-footer">
                                <button type="button" className="data-admin-btn data-admin-btn-secondary" onClick={closeModal} disabled={loading}>Batal</button>
                                <button type="submit" className="data-admin-btn data-admin-btn-primary" disabled={loading}>
                                    {loading ? <><div className="data-admin-spinner"></div> Menyimpan...</> : <><i className="fas fa-save"></i> Simpan</>}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            );
        }

        if (isDelete) {
            const itemName = isKL ? currentItem.nama_kl : isChecklist ? currentItem.description : isKepka ? `Kepka No. ${currentItem.no}` : currentItem.nama_igt;
            const handleDelete = isKL ? handleDeleteKL : isChecklist ? handleDeleteChecklist : isKepka ? handleDeleteKepka : handleDeleteIGT;
            return (
                <div className="data-admin-modal-overlay" onClick={closeModal}>
                    <div className="data-admin-modal data-admin-modal-confirm" onClick={(e) => e.stopPropagation()}>
                        <div className="data-admin-modal-header">
                            <h3><i className="fas fa-exclamation-triangle"></i> Konfirmasi Hapus</h3>
                            <button className="data-admin-modal-close" onClick={closeModal} disabled={loading}><i className="fas fa-times"></i></button>
                        </div>
                        <div className="data-admin-modal-body">
                            <div className="data-admin-confirm-content">
                                <p>Apakah Anda yakin ingin menghapus <strong>"{itemName}"</strong>?</p>
                                <div className="data-admin-confirm-warning">
                                    <i className="fas fa-exclamation-circle"></i>
                                    Tindakan ini tidak dapat dibatalkan. Data yang terkait tidak akan bisa dihapus jika masih digunakan.
                                </div>
                            </div>
                        </div>
                        <div className="data-admin-modal-footer">
                            <button type="button" className="data-admin-btn data-admin-btn-secondary" onClick={closeModal} disabled={loading}>Batal</button>
                            <button type="button" className="data-admin-btn data-admin-btn-danger" onClick={handleDelete} disabled={loading}>
                                {loading ? <><div className="data-admin-spinner"></div> Menghapus...</> : <><i className="fas fa-trash-alt"></i> Ya, Hapus</>}
                            </button>
                        </div>
                    </div>
                </div>
            );
        }

        if (isUsage) {
            const itemName = isKL ? currentItem.nama_kl : currentItem.nama_igt;
            return (
                <div className="data-admin-modal-overlay" onClick={closeModal}>
                    <div className="data-admin-modal data-admin-modal-large" onClick={(e) => e.stopPropagation()}>
                        <div className="data-admin-modal-header">
                            <h3><i className="fas fa-info-circle"></i> Detail Penggunaan: {itemName}</h3>
                            <button className="data-admin-modal-close" onClick={closeModal}><i className="fas fa-times"></i></button>
                        </div>
                        <div className="data-admin-modal-body">
                            {loading && <div className="data-admin-spinner-container">Memuat data...</div>}
                            {usageData && (
                                <div className="data-admin-usage-container">
                                    {isKL && (
                                        <>
                                            <div className="data-admin-usage-section">
                                                <h4><i className="fas fa-users"></i> Digunakan oleh Users ({usageData.users?.length || 0})</h4>
                                                {usageData.users?.length > 0 ? (
                                                    <div className="data-admin-usage-list">
                                                        {usageData.users.map((u, index) => <div key={`usage-user-${u.username}-${index}`} className="data-admin-usage-item"><span className="data-admin-usage-name">{u.fullname}</span> <span className="data-admin-usage-detail">@{u.username}</span></div>)}
                                                    </div>
                                                ) : <div className="data-admin-usage-empty"><i className="fas fa-check-circle"></i>Tidak digunakan oleh user.</div>}
                                            </div>
                                            <div className="data-admin-usage-section">
                                                <h4><i className="fas fa-database"></i> Digunakan di Tabel Data ({usageData.data?.length || 0})</h4>
                                                {usageData.data?.length > 0 ? (
                                                    <div className="data-admin-usage-list">
                                                        {usageData.data.map((d, index) => <div key={`usage-data-${d.no_tiket}-${index}`} className="data-admin-usage-item"><span className="data-admin-usage-name">{d.nama_igt}</span> <span className="data-admin-usage-detail">Tiket: {d.no_tiket}</span></div>)}
                                                    </div>
                                                ) : <div className="data-admin-usage-empty"><i className="fas fa-check-circle"></i>Tidak digunakan di tabel data.</div>}
                                            </div>
                                        </>
                                    )}
                                    <div className="data-admin-usage-section">
                                        <h4><i className="fas fa-map-marked-alt"></i> Digunakan di Data IGT ({isKL ? usageData.data_igt?.length : usageData.data?.length || 0})</h4>
                                        {(isKL ? usageData.data_igt?.length > 0 : usageData.data?.length > 0) ? (
                                            <div className="data-admin-usage-list">
                                                {(isKL ? usageData.data_igt : usageData.data).map((d, index) => <div key={`usage-dataigt-${d.kode_kl}-${d.kode_igt}-${index}`} className="data-admin-usage-item"><span className="data-admin-usage-name">{d.nama_igt}</span> <span className="data-admin-usage-detail">{d.kode_igt ? `Kode: ${d.kode_igt}` : `Tiket: ${d.no_tiket}`}</span></div>)}
                                            </div>
                                        ) : <div className="data-admin-usage-empty"><i className="fas fa-check-circle"></i>Tidak digunakan di data IGT.</div>}
                                    </div>
                                    <div className="data-admin-usage-section">
                                        <h4><i className="fas fa-archive"></i> Digunakan di Arsip IGT ({usageData.archived_igt?.length || 0})</h4>
                                        {usageData.archived_igt?.length > 0 ? (
                                            <div className="data-admin-usage-list">
                                                {usageData.archived_igt.map((a, index) => <div key={`usage-archive-${a.no_tiket}-${a.kode_igt}-${index}`} className="data-admin-usage-item"><span className="data-admin-usage-name">{a.nama_igt}</span> <span className="data-admin-usage-detail">{a.kode_igt ? `Kode: ${a.kode_igt}` : `Tiket: ${a.no_tiket}`}</span></div>)}
                                            </div>
                                        ) : <div className="data-admin-usage-empty"><i className="fas fa-check-circle"></i>Tidak digunakan di arsip IGT.</div>}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            );
        }

        return null;
    };

    const getPageTitle = () => {
        switch(activeTab) {
            case 'kl': return 'Kementerian/Lembaga';
            case 'data-igt': return 'Data IGT';
            case 'checklist': return 'Checklist Dokumen';
            default: return 'Manajemen Data';
        }
    };

    const getPageSubtitle = () => {
        switch(activeTab) {
            case 'kl': return 'Kelola data Kementerian dan Lembaga';
            case 'data-igt': return 'Kelola Data Informasi Geospasial Tematik';
            case 'checklist': return 'Kelola item checklist untuk pengecekan dokumen';
            default: return 'Kelola data master sistem';
        }
    };

    return (
        <DashboardLayout menuItems={getAdminMenuItems()} userRole="Admin">
            <ToastContainer 
                position="top-center" 
                autoClose={5000} 
                hideProgressBar={false}
                newestOnTop
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                theme="colored"
                style={{ zIndex: 99999 }}
            />
            <div className="management-tabs">
                <button 
                    className={`management-tab ${activeTab === 'kl' ? 'active' : ''}`}
                    onClick={() => setActiveTab('kl')}
                >
                    <i className="fas fa-building"></i>
                    <span>Kementerian/Lembaga</span>
                    <span className="tab-counter">{filteredKL.length}</span>
                </button>
                <button 
                    className={`management-tab ${activeTab === 'data-igt' ? 'active' : ''}`}
                    onClick={() => setActiveTab('data-igt')}
                >
                    <i className="fas fa-map-marked-alt"></i>
                    <span>Data IGT</span>
                    <span className="tab-counter">{filteredIGT.length}</span>
                </button>
                <button 
                    className={`management-tab ${activeTab === 'checklist' ? 'active' : ''}`}
                    onClick={() => setActiveTab('checklist')}
                >
                    <i className="fas fa-clipboard-check"></i>
                    <span>Checklist</span>
                    <span className="tab-counter">{filteredChecklist.length}</span>
                </button>
            </div>

            <div className="management-page-header-modern">
                <div className="management-header-icon">
                    <i className={`fas ${activeTab === 'kl' ? 'fa-building' : activeTab === 'data-igt' ? 'fa-map-marked-alt' : 'fa-clipboard-check'}`}></i>
                </div>
                <div className="management-header-content">
                    <h1 className="management-page-title-modern">
                        {getPageTitle()}
                    </h1>
                    <p className="management-page-subtitle-modern">
                        {getPageSubtitle()}
                    </p>
                </div>
                <div className="management-page-actions">
                    {activeTab === 'kl' && (
                        <button className="management-btn management-btn-primary" onClick={() => openModal('addKL')}>
                            <i className="fas fa-plus"></i>
                            <span>Tambah KL</span>
                        </button>
                    )}
                    {activeTab === 'data-igt' && (
                        <button className="management-btn management-btn-primary" onClick={() => openModal('addIGT')}>
                            <i className="fas fa-plus"></i>
                            <span>Tambah Data IGT</span>
                        </button>
                    )}
                    {activeTab === 'checklist' && (
                        <button className="management-btn management-btn-primary" onClick={() => openModal('addChecklist')}>
                            <i className="fas fa-plus"></i>
                            <span>Tambah Item Checklist</span>
                        </button>
                    )}
                </div>
            </div>

            {/* Content Card */}
            <div className="management-card">
                {activeTab === 'kl' ? renderKLTab() : activeTab === 'data-igt' ? renderDataIGTTab() : renderChecklistTab()}
            </div>

            {renderModal()}
        </DashboardLayout>
    );
};

export default ManajemenDataPage;